import axios from "../config/axiosInstance";

// Cache em memória para dados de referência do board (estrutura do funil,
// usuários, campos personalizados). Evita re-bater no servidor toda vez que se
// entra no CRM — esses fetches são axios cru em useEffect e re-disparam no
// mount.
//
// Vive fora do CRMTable de propósito: qualquer tela que altere funil ou etapa
// precisa conseguir invalidá-lo sem importar um componente de 12 mil linhas.
const _cache = new Map(); // key -> { res, expiresAt }
const _inflight = new Map(); // key -> Promise

// Contador de invalidações. Uma resposta que começou antes de um invalidate não
// pode ser gravada depois dele: era esse o motivo de a tela só atualizar com
// F5 — o GET em voo regravava o dado antigo por cima do cache já limpo, e o
// Map só morria de verdade no reload da página.
let _epoch = 0;

export async function cachedGet(key, url, ttlMs = 120000) {
  const hit = _cache.get(key);
  if (hit && hit.expiresAt > Date.now()) return hit.res;

  // Dedup: várias abas do board montando ao mesmo tempo pediam a mesma URL.
  const pending = _inflight.get(key);
  if (pending) return pending;

  const epochAtStart = _epoch;
  const promise = axios
    .get(url)
    .then((res) => {
      if (_epoch === epochAtStart) {
        _cache.set(key, { res, expiresAt: Date.now() + ttlMs });
      }
      return res;
    })
    .finally(() => {
      _inflight.delete(key);
    });

  _inflight.set(key, promise);
  return promise;
}

export function invalidateCrmRefCache(prefix) {
  _epoch += 1;
  if (!prefix) {
    _cache.clear();
    return;
  }
  for (const k of _cache.keys()) {
    if (String(k).startsWith(prefix)) _cache.delete(k);
  }
}

export function clearCrmRefCache() {
  invalidateCrmRefCache();
}

/**
 * Invalidação automática em qualquer escrita de estrutura do CRM.
 *
 * Antes, só o rename de etapa dentro do CRMTable limpava o cache. Editar o
 * funil pelo "Gerenciar funis" ou pelo inspetor de etapa não limpava nada, e o
 * board seguia mostrando o valor antigo até o TTL vencer — ou até o F5, que é o
 * único jeito de matar um Map em memória.
 *
 * Fazer isso no interceptor cobre também as telas que ainda não existem: não
 * depende de cada autor lembrar de invalidar.
 */
const STRUCTURE_WRITE = /\/crm\/(funnels|stages)|\/custom-fields/i;
// Escritas em CONTATOS (criar, editar, excluir, importar, converter, vincular):
// o quadro guarda a lista de contatos do modal "criar card" (`clients-20`, 60 s).
// Sem isto, um contato recém-criado (na tela de contatos, no WhatsApp, por
// importação, no gerador de leads ou no orçamento) não aparecia ao criar o card.
const LEAD_WRITE = /\/leads(\/|$|\?)/i;
// Escritas em USUÁRIOS: `users-all` (180 s) alimenta o seletor de responsável.
const USER_WRITE = /\/users?(\/|$|\?)/i;
// Mover card não muda a estrutura do funil; limpar aqui só geraria refetch à toa.
const CARD_ONLY = /\/cards(\/|$|\?)/i;

axios.interceptors.response.use((response) => {
  const method = String(response?.config?.method || "").toLowerCase();
  const url = String(response?.config?.url || "");

  if (method !== "get" && STRUCTURE_WRITE.test(url) && !CARD_ONLY.test(url)) {
    invalidateCrmRefCache("funnel-details-");
    invalidateCrmRefCache("fields-");
    invalidateCrmRefCache("public-forms-");
    invalidateCrmRefCache("lead-custom-fields");
  }

  if (method !== "get") {
    if (LEAD_WRITE.test(url)) invalidateCrmRefCache("clients-");
    if (USER_WRITE.test(url)) invalidateCrmRefCache("users-all");
  }

  return response;
});
